import * as Honeycomb from 'honeycomb-grid';

// Re-export the core types for type safety in the rest of the app.
export type { Hex, Point } from 'honeycomb-grid';
export type Grid<T extends Honeycomb.Hex> = Honeycomb.Grid<T>;

/**
 * This service acts as an adapter for the honeycomb-grid library.
 * It is the ONLY module in the application that should directly import and use honeycomb-grid.
 * This isolates the external library and provides a stable, consistent internal API.
 */

export interface GridSettings {
  orientation: 'pointy' | 'flat';
  size: number;
  offset: -1 | 1;
  origin: Honeycomb.Point;
}

export class GridService {
  private HexFactory?: ReturnType<typeof Honeycomb.defineHex>;

  constructor() {
    // The constructor is now empty. The service is inert until `configure` is called.
    // It no longer holds a default grid instance.
  }

  /**
   * Configures the grid service with new settings. This must be called
   * before most other methods can be used.
   */
  configure(settings: GridSettings): void {
    this.HexFactory = Honeycomb.defineHex({
      dimensions: settings.size,
      orientation: settings.orientation,
      origin: settings.origin,
      offset: settings.offset,
    });
  }

  public getFactory(): ReturnType<typeof Honeycomb.defineHex> {
    if (!this.HexFactory) {
      throw new Error('GridService has not been configured. Call configure() first.');
    }
    return this.HexFactory;
  }

  rectangle(options: { width: number; height: number; start?: { q?: number; r?: number } | { col?: number; row?: number }; }): Honeycomb.Hex[] {
    const factory = this.getFactory();
    // The honeycomb-grid library throws an error if `start` is an object with undefined properties.
    // We ensure that if `start` is provided, it's valid, otherwise we default to the origin. This also satisfies TypeScript's type checker.
    const rectangleOptions: Parameters<typeof Honeycomb.rectangle>[0] = {
      width: options.width,
      height: options.height,
      start: options.start && (('q' in options.start && options.start.q !== undefined) || ('col' in options.start && options.start.col !== undefined)) ? (options.start as { q: number; r: number }) : { q: 0, r: 0 },
    };
    // Create a temporary grid to correctly execute the traverser
    const grid = new Honeycomb.Grid(factory, Honeycomb.rectangle(rectangleOptions));
    return grid.toArray();
  }

  /**
   * Creates a rectangle of hexes starting from a pixel coordinate.
   */
  rectangleFromPoint(options: { width: number; height: number; start?: Honeycomb.Point; }): Honeycomb.Hex[] {
    const factory = this.getFactory();
    // Convert the pixel point to hex coordinates before creating the rectangle.
    const startHex = options.start ? this.pointToHex(options.start) : null;

    // We must construct the options carefully. Spreading `...options` would include the original
    // `start` point, causing a type error. We only include `start` if it's a valid hex.
    const rectangleOptions: Parameters<typeof Honeycomb.rectangle>[0] = { width: options.width, height: options.height };
    if (startHex) {
      rectangleOptions.start = startHex;
    }
    // Create a temporary grid to correctly execute the traverser
    const grid = new Honeycomb.Grid(factory, Honeycomb.rectangle(rectangleOptions));
    return grid.toArray();
  }

  /**
   * Converts pixel coordinates to a Hex.
   */
  pointToHex(point: Honeycomb.Point): Honeycomb.Hex {
    // This method now uses the factory directly, as it doesn't need a pre-populated grid.
    // It creates a temporary, single-hex grid to perform the conversion.
    const factory = this.getFactory();
    const tempGrid = new Honeycomb.Grid(factory);
    return tempGrid.pointToHex(point);
  }

  /**
   * Gets a Hex from the grid by its axial coordinates.
   * The honeycomb-grid library uses 'x' and 'y' for what are often called 'q' and 'r' in axial systems.
   */
   
  getHex(_coords: { q: number; r: number }): Honeycomb.Hex | undefined {
    // This method is no longer reliable as there's no single "main" grid.
    // It's better to create grids on-demand in the strategies.
    throw new Error('getHex is deprecated. Create a grid in the strategy to get hexes.');
  }

  /**
   * Returns a grid of hexes forming a line between two hexes.
   */
  getHexesBetween(hexA: Honeycomb.Hex, hexB: Honeycomb.Hex): Honeycomb.Hex[] {
    // line() directly returns a Hex[] array.
    return Honeycomb.line({ start: hexA, stop: hexB });
  }

  /**
   * Returns an array of hexes in a cone shape.
   */
  getCone(options: { start: Honeycomb.Hex; radius: number; direction: number }): Honeycomb.Hex[] {
    // cone() directly returns a Hex[] array.
    return Honeycomb.cone(options);
  }

  /**
   * Calculates the distance between two hexes.
   */
  distance(hexA: Honeycomb.Hex, hexB: Honeycomb.Hex): number {
    // Distance is a static-like method on the Grid class.
    // It needs a factory to understand the hexes, so we create a temporary grid.
    const factory = this.getFactory();
    return new Honeycomb.Grid(factory).distance(hexA, hexB);
  }

  /**
   * Returns an array of hexes within a certain range of a center hex.
   */
  getHexesInRange(center: Honeycomb.Hex, range: number): Honeycomb.Hex[] {
    const factory = this.getFactory();
    // Create a temporary grid centered on the hex to calculate the range.
    const grid = new Honeycomb.Grid(factory, Honeycomb.ring({ center, radius: range }));
    // Add the center hex itself to the result.
    return [...grid.toArray(), center];
  }
}